import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import './PlacementDetailsModal.css';

const PlacementDetailsModal = ({ isOpen, onClose, selectedRecord }) => {
  const [placementData, setPlacementData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch placement data when modal opens and selectedRecord changes
  useEffect(() => {
    const fetchPlacementData = async () => {
      if (!isOpen || !selectedRecord || !selectedRecord.studentId && !selectedRecord.id) {
        return;
      }

      // Get studentId from selectedRecord
      const studentId = selectedRecord.studentId || selectedRecord.id;
      
      try {
        setLoading(true);
        setError('');

        // Get authentication token
        const token = localStorage.getItem('authToken') || 
                     sessionStorage.getItem('authToken') || 
                     localStorage.getItem('token');

        const response = await fetch(`${process.env.REACT_APP_API_URL}/studentplacement/${studentId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` // Adjust auth header format as needed
          }
        });

        if (!response.ok) {
          if (response.status === 404) {
            // No placement records found
            setPlacementData([]);
            return;
          }
          throw new Error(`Failed to fetch placement data: ${response.status}`);
        }

        const result = await response.json();
        
        // Handle different response structures
        const data = result.data || result.placements || result;
        
        // Ensure data is an array
        const placementArray = Array.isArray(data) ? data : [data];
        
        setPlacementData(placementArray);

      } catch (error) {
        console.error('Error fetching placement data:', error);
        setError(error.message || 'Failed to load placement data');
        setPlacementData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlacementData();
  }, [isOpen, selectedRecord]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPlacementData([]);
      setError('');
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h3>Placement Details</h3>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="modal-body">
          <div className="student-info">
            <h4>Student: {selectedRecord?.studentName || 'N/A'}</h4>
            <p>Course: {selectedRecord?.courseName || 'N/A'}</p>
            <p>Student ID: {selectedRecord?.studentId || selectedRecord?.id || 'N/A'}</p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="loading-container" style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '40px',
              flexDirection: 'column'
            }}>
              <FontAwesomeIcon icon={faSpinner} spin size="2x" />
              <p style={{ marginTop: '10px' }}>Loading placement data...</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="error-container" style={{
              color: '#dc3545',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px',
              padding: '15px',
              margin: '10px 0',
              textAlign: 'center'
            }}>
              <p>{error}</p>
              <button 
                onClick={() => window.location.reload()} 
                style={{
                  marginTop: '10px',
                  padding: '5px 15px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            </div>
          )}

          {/* No Data State */}
          {!loading && !error && placementData.length === 0 && (
            <div className="no-data-container" style={{
              textAlign: 'center',
              padding: '40px',
              color: '#6c757d'
            }}>
              <p>No placement records found for this student.</p>
            </div>
          )}

          {/* Data Table */}
          {!loading && !error && placementData.length > 0 && (
            <div className="placement-table-container">
              <table className="placement-details-table">
                <thead>
                  <tr>
                    <th>Company Name</th>
                    <th>Status</th>
                    <th>Company Feedback</th>
                    <th>Placement Officer Feedback</th>
                    <th>Student Feedback</th>
                    <th>Date Created</th>
                  </tr>
                </thead>
                <tbody>
                  {placementData.map((item, index) => (
                    <tr key={item.id || index}>
                      <td className="company-name">
                        {item.companyName || 'N/A'}
                      </td>
                      <td className={`status status-${(item.status || '').toLowerCase().replace(/\s+/g, '-')}`}>
                        {item.status || 'N/A'}
                      </td>
                      <td className="feedback-cell">
                        {item.companyFeedback || 'No feedback provided'}
                      </td>
                      <td className="feedback-cell">
                        {item.placementOfficerFeedback || 'No feedback provided'}
                      </td>
                      <td className="feedback-cell">
                        {item.studentFeedback || 'No feedback provided'}
                      </td>
                      <td className="date-cell">
                        {item.createdAt 
                          ? new Date(item.createdAt).toLocaleDateString() 
                          : item.updatedAt 
                            ? new Date(item.updatedAt).toLocaleDateString()
                            : 'N/A'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlacementDetailsModal;